import { t } from "i18next";
import { useMemo, useState } from "react";

import Chevron from "@/assets/icons/chevron.svg?react";
import LevelCircle from "@/assets/images/achievements/level-circle.svg?react";
import Level from "@/assets/images/achievements/level.svg?react";
import { useStore } from "@/hooks/store";
import { Achievement } from "@/interfaces/api";
import { useModalContext } from "@/providers/modal-provider/context";
import { isHapiAchievementType, toCapitalized, transformStringWithUpperHAPI } from "@/utils";

import { AchievementLevel } from "./AchievementLevel";
import styles from "./styles";
import { achievementMap, hapiAchievementActionMap } from "./utils";
import { SlideDown } from "../SlideDown";

export const Achievements = ({ handler }: { handler?: (isOpen: boolean) => void }) => {
  const modal = useModalContext();

  const achievements = useStore((store) => store.user.achievements);

  const [isOpen, setIsOpen] = useState(false);

  const { yours, common, hereAchievements, hapiAchievements } = useMemo(() => {
    return (achievements || []).reduce(
      (acc, achievement) => {
        acc.common += achievement?.levels?.length;

        acc.yours += achievement?.levels?.reduce((levelAcc, level) => {
          return level.complete ? levelAcc + 1 : levelAcc;
        }, 0);

        if (isHapiAchievementType(achievement.id)) {
          acc.hapiAchievements.push(achievement);
        } else {
          acc.hereAchievements.push(achievement);
        }

        return acc;
      },
      { yours: 0, common: 0, hereAchievements: [] as Array<Achievement>, hapiAchievements: [] as Array<Achievement> },
    );
  }, [achievements]);

  if (!achievements?.length) return null;

  return (
    <styles.Container>
      <styles.Header
        onClick={() => {
          handler?.(!isOpen);
          setIsOpen(!isOpen);
        }}
        $isOpen={isOpen}
      >
        <styles.TripleLevel>
          <Level />
          <Level />
          <Level />
          <span>I</span>
        </styles.TripleLevel>
        <p>{t("Achievements.Title")}</p>
        <styles.NumberAchievements>
          <span>{yours}</span>
          <span>/{common}</span>
        </styles.NumberAchievements>
        <Chevron />
      </styles.Header>
      <SlideDown show={isOpen}>
        <styles.Body>
          <p>{t("Achievements.Description")}</p>
          <styles.Achievements>
            {hapiAchievements.length ? (
              <styles.PrimeAchievements>
                {hapiAchievements.map(({ id, levels }) => (
                  <styles.Achievement.Container key={id}>
                    <styles.Achievement.Image $type={id}>
                      <img src={achievementMap[id]} alt={id} />
                    </styles.Achievement.Image>
                    <div>
                      <styles.Achievement.Title>
                        {t("Achievements.The", { achievement: transformStringWithUpperHAPI(id) })}
                      </styles.Achievement.Title>
                      <styles.Achievement.Levels>
                        {levels.map((level, index, arr) => (
                          <AchievementLevel
                            key={index}
                            level={level}
                            index={index}
                            levels={arr}
                            iconLevel={<LevelCircle />}
                            handler={() =>
                              modal.achievements.openModal({
                                title: t("Achievements.The", { achievement: transformStringWithUpperHAPI(id) }),
                                description: level.text,
                                level: {
                                  id: index + 1,
                                  icon: <LevelCircle />,
                                  complete: level.complete,
                                },
                                action: hapiAchievementActionMap[index + 1],
                              })
                            }
                          />
                        ))}
                      </styles.Achievement.Levels>
                    </div>
                  </styles.Achievement.Container>
                ))}
              </styles.PrimeAchievements>
            ) : null}
            {hereAchievements.map(({ id, levels }) => (
              <styles.Achievement.Container key={id}>
                <styles.Achievement.Image $type={id}>
                  <img src={achievementMap[id]} alt={id} />
                </styles.Achievement.Image>
                <div>
                  <styles.Achievement.Title>
                    {t("Achievements.The", { achievement: toCapitalized(id) })}
                  </styles.Achievement.Title>
                  <styles.Achievement.Levels>
                    {levels.map((level, index, arr) => (
                      <AchievementLevel
                        key={index}
                        level={level}
                        index={index}
                        levels={arr}
                        handler={() =>
                          modal.achievements.openModal({
                            title: t("Achievements.The", { achievement: toCapitalized(id) }),
                            description: level.text,
                            level: {
                              id: index + 1,
                              icon: <Level />,
                              complete: level.complete,
                            },
                          })
                        }
                      />
                    ))}
                  </styles.Achievement.Levels>
                </div>
              </styles.Achievement.Container>
            ))}
          </styles.Achievements>
        </styles.Body>
      </SlideDown>
    </styles.Container>
  );
};
